/**
 * The global Vuex store. The store is separated by entity in sub-stores.
 *
 * This file is the store entrypoint as well as the global store structure definition.
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import { KpState } from '@/@types/store/state';
import Vue from 'vue';
import Vuex, { Store } from 'vuex';
import album from './modules/album';
import banner from './modules/banner';
import category from './modules/category';
import weblink from './modules/weblink';
import country from './modules/country';
import dossier from './modules/dossier';
import eventfrog_rubric from './modules/eventfrog_rubric';
import genre_group from './modules/genre_group';
import image from './modules/image';
import mailing_list from './modules/mailing_list';
import organizer_group from './modules/organizer_group';
import page from './modules/page';
import region from './modules/region';
import schedule_status from './modules/schedule_status';
import tag from './modules/tag';
import tag_group from './modules/tag_group';
import target_audience from './modules/target_audience';
import interface_value from './modules/interface_value';
import user from './modules/user';
import zip from './modules/zip';

Vue.use(Vuex);

let _store: Store<KpState>;
const initialState: KpState = {
    session: null,
    appVersion: '',
    loading: false,
    loadingCounter: 0,
    snackbarContent: '',
    customer: null,
};

const init = function (initialData?: KpState) {
    _store = new Vuex.Store({
        state: { ...initialState, ...initialData } as KpState,
        mutations: {
            setSession(state, session) {
                state.session = session;
            },

            loading(state, loading) {
                if (loading) {
                    state.loadingCounter++;
                } else {
                    state.loadingCounter = Math.max(0, state.loadingCounter - 1);
                }
                state.loading = state.loadingCounter > 0;
            },
            toast(state, payload) {
                state.snackbarContent = payload.content;
            },
            // TODO: maybe used later...
            // updateDataCache(state, { key, data }) {
            //     state.dataCache[key] = data;
            // },
        },
        actions: {
            async runAsync({ commit }, runner) {
                commit('loading', true);
                let res = null;
                try {
                    res = await runner();
                } finally {
                    commit('loading', false);
                }
                return res;
            },

            // TODO: maybe used later...
            // updateDataCache({ commit }, { key, data }) {
            //     commit('updateDataCache', { key, data });
            // },
        },

        getters: {
            isAuthenticated: (state: KpState) => state.user?.record !== null && state.user?.record?.id > 0,
            isAdmin: (state) => state.user?.record !== null && state.user?.record.isAdmin,
            user: (state) => state.user?.record,

            /**
             * TODO: Maybe used later...
             * Fetch an entry by key from the dataCache store
             * Use as getter: store.getters.getDataCacheEntry(key)
             */
            // getDataCacheEntry: (state: KpState) => (key) => {
            //     return state.dataCache[key] || null;
            // },
        },
        modules: {
            album,
            banner,
            category,
            country,
            dossier,
            eventfrog_rubric,
            genre_group,
            image,
            interface_value,
            mailing_list,
            organizer_group,
            page,
            region,
            schedule_status,
            tag,
            tag_group,
            target_audience,
            user,
            weblink,
            zip,
        },
    });
    return _store;
};

export const getStore = function () {
    if (!_store) {
        throw new Error('Store is not initialized. Use init().');
    }
    return _store;
};

export default {
    init,
    getStore,
};
